import { useMedplum } from '@medplum/react-hooks';
import { Observation } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';
import { useSubscription } from '@medplum/react';
import { EXTERNAL_CS } from '@/consts/systems.consts';

const growthChartCodes = [
  EXTERNAL_CS.LOINC.CODES.BODY_HEIGHT,
  EXTERNAL_CS.LOINC.CODES.BODY_WEIGHT,
  EXTERNAL_CS.LOINC.CODES.BMI,
  EXTERNAL_CS.LOINC.CODES.HEAD_CIRCUMFERENCE,
  EXTERNAL_CS.SNOMED.CODES.GESTATIONAL_AGE,
  EXTERNAL_CS.SNOMED.CODES.BIRTH_WEIGHT,
  EXTERNAL_CS.SNOMED.CODES.BIRTH_LENGTH,
  EXTERNAL_CS.SNOMED.CODES.BIRTH_HEAD_CIRCUMFERENCE,
];

export const usePatientMeasurments = (patientId?: string) => {
  const medplum = useMedplum();
  const [data, setData] = useState<Observation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useSubscription(
    patientId
      ? `Observation?subject=Patient/${patientId}&code=${growthChartCodes.join(
          ','
        )}`
      : `Observation?subject=Patient/123&code=${EXTERNAL_CS.LOINC.CODES.BODY_HEIGHT}`,
    (bundle) => {
      const newObservation = bundle.entry?.find(
        (o) => o.resource?.resourceType === 'Observation'
      )?.resource as Observation | undefined;

      if (!newObservation?.id) {
        return;
      }

      setData((prev) => {
        const existing = prev.find((o) => o.id === newObservation.id);

        if (existing) {
          // Update existing observation
          return prev.map((o) =>
            o.id === newObservation.id ? newObservation : o
          );
        } else {
          // Add new obervation to the array
          return [...prev, newObservation];
        }
      });
    }
  );

  useEffect(() => {
    if (!patientId) {
      setData([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchGrowthChartData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const codes = growthChartCodes.join(',');

        // This will automatically fetch all pages
        const allObservations: Observation[] = [];
        for await (const page of medplum.searchResourcePages('Observation', {
          patient: patientId,
          code: codes,
        })) {
          const observations = page
            .filter((e) => e.resourceType === 'Observation')
            .map((e) => e as Observation);
          allObservations.push(...observations);
        }

        setData(allObservations);
      } catch (error) {
        setError(error as Error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGrowthChartData();
  }, [patientId, medplum]);

  return { data, isLoading, error };
};

export default usePatientMeasurments;
